import Phaser from 'phaser';
import { EventBus, ELEVATOR_SPRITES, deskItemDepthOffsets, findFurniture, nextOrientation, findWallStyle, wallPreviewFrames, doorsCatalog, findDoor, baseOnlyCollisionGrid, standingPlacement, furnitureSortY } from '@campus/core';
import type { DoorCatalogEntry, FloorLayerData, FloorLayerName, FloorEdit, GameEventMap } from '@campus/core';
import { applyFloorEdits, emptyFloorLayer, floorValueAt } from '@campus/core';
import type { ZoneConfig, ZoneAssetUrls } from '@campus/core';
import { ZoneLoader } from '../loaders/ZoneLoader.js';
import { WallRenderer, preloadWallAtlas, WALL_ATLAS_KEY } from '../loaders/WallRenderer.js';
import { FloorRenderer, preloadFloorAtlas, FLOOR_DEPTHS } from '../loaders/FloorRenderer.js';
import { LabelRenderer } from '../loaders/LabelRenderer.js';
import { addExtrudedTilesetImage } from '../loaders/extrudeTiles.js';
import { ensureFurnitureTextures, hasFurnitureTexture, spawnFurnitureImage } from '../loaders/FurnitureTextures.js';
import { useGameStore } from '@campus/core';
import { getTenantSlug } from '../../lib/tenant.js';
import { API_BASE } from '../../lib/apiBase.js';
import { attachWheelZoom } from '../../lib/zoom.js';

interface PlacedFurniture {
  dbId: string;
  furnitureId: string;
  tileX: number;
  tileY: number;
  layer: string;
  sprite: Phaser.GameObjects.Image;
}

const TILE = 32;

type ZoneType = 'desk' | 'bubble' | 'door' | 'portal' | 'spawn' | 'meeting-room' | 'desk-area' | 'elevator';

const ZONE_COLORS: Record<ZoneType, number> = {
  desk: 0x4F6EF7,
  bubble: 0xA8F0C6,
  door: 0xFFD166,
  portal: 0xF07070,
  spawn: 0x9B59B6,
  'meeting-room': 0x2EC4B6,
  'desk-area': 0xF4A261,
  elevator: 0x8FB4E8,
};

interface PlacedWall {
  dbId: string;
  tileX: number;
  tileY: number;
  wallStyleId: string;
}

interface PlacedZone {
  dbId: string;
  type: ZoneType;
  name: string;
  properties: Record<string, unknown>;
  x: number;
  y: number;
  width: number;
  height: number;
  graphics: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  /** Zones de type porte : sprite de la porte (frame fermée), comme en jeu. */
  doorSprite?: Phaser.GameObjects.Image;
}

/**
 * EditorScene — Scene Phaser pour l'editeur de carte admin.
 *
 * Mode solo : pas de Colyseus, pas de LiveKit, pas d'avatars.
 * Affiche la carte statique avec une grille overlay et une camera libre.
 * Gere le placement/suppression de mobilier (ME-2d).
 */
export class EditorScene extends Phaser.Scene {
  private zoneConfig: ZoneConfig | null = null;
  private zoneUrls: ZoneAssetUrls | null = null;
  private zoneLoader!: ZoneLoader;

  private gridGraphics: Phaser.GameObjects.Graphics | null = null;
  private mapWidth = 0;
  private mapHeight = 0;

  /** Mode courant, modifiable depuis React via EventBus. */
  currentMode: 'select' | 'furniture' | 'zone' | 'wall' | 'door' | 'floor' | 'text' | 'slots' = 'select';

  // Camera drag state
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;

  // Furniture placement state (ME-2d)
  private selectedFurnitureId: string | null = null;
  private selectedFurnitureUrl: string | null = null;
  private selectedFurnitureLayer: string = 'furniture';
  private selectedFurnitureIsSolid: boolean = false;
  private selectedFurnitureSolidBaseOnly: boolean = false;
  private ghostSprite: Phaser.GameObjects.Image | null = null;
  private placedFurniture: PlacedFurniture[] = [];
  // Meuble posé sélectionné (clic dessus en mode Sélection, ou en Mobilier
  // sans objet choisi) : R change sa vue.
  private selectedPlaced: PlacedFurniture | null = null;
  private selectionOutline: Phaser.GameObjects.Graphics | null = null;
  private clickStart: { x: number; y: number } | null = null;

  // Zone drawing state (ME-3d)
  private selectedZoneType: ZoneType | null = null;
  private isDrawingZone = false;
  private zoneDrawStartTileX = 0;
  private zoneDrawStartTileY = 0;
  private zoneDrawPreview: Phaser.GameObjects.Graphics | null = null;
  private placedZones: PlacedZone[] = [];
  private selectedZoneId: string | null = null;

  // Glisser-déposer d'une zone existante (déplacement, pas redimensionnement)
  private draggingZoneId: string | null = null;
  private dragZoneStartTileX = 0;
  private dragZoneStartTileY = 0;
  private dragZoneOrigX = 0;
  private dragZoneOrigY = 0;

  private personalDeskObjects: Phaser.GameObjects.GameObject[] = [];
  // Emplacements de bureaux (mode « Emplacements », tasks/emplacements-bureaux-plan.md).
  private deskSlots: GameEventMap['editor:desk-slots']['slots'] = [];
  private deskSlotObjects: Phaser.GameObjects.GameObject[] = [];
  private personalDesks: GameEventMap['editor:personal-desks']['desks'] = [];
  private slotDrag: {
    /** 'slot' : un emplacement (et son bureau éventuel) ; 'desk' : un bureau posé hors emplacement. */
    kind: 'slot' | 'desk';
    id: string;
    /** Bureau posé sur l'emplacement glissé (kind 'slot'). */
    deskId: string | null;
    offsetX: number; offsetY: number;
    /** Case sous la souris (cible d'un emplacement libre). */
    pointerX: number; pointerY: number;
    /** Origine de l'espace (cases) : position relative = case - origine. */
    areaX: number; areaY: number;
    startTileX: number; startTileY: number; tileX: number; tileY: number;
    width: number; height: number; ghost: Phaser.GameObjects.Graphics;
  } | null = null;
  private personalDeskGeneration = 0;

  // Garde contre un double-appel de shutdown() (SHUTDOWN + DESTROY peuvent
  // en théorie tous les deux se déclencher — voir Systems.js de Phaser).
  private hasShutdown = false;

  // Wall placement state
  private selectedWallStyleId: string | null = null;
  private wallGhost: Phaser.GameObjects.Container | null = null;
  private placedWalls: PlacedWall[] = [];
  private wallRenderer: WallRenderer | null = null;
  // Tuiles dont le POST est en vol — évite un double POST si le glisser
  // repasse sur la même tuile avant la réponse de l'API.
  private pendingWallTiles = new Set<string>();

  // Door placement state (mode "Portes")
  private selectedDoorId: string | null = null;
  private doorGhost: Phaser.GameObjects.Image | null = null;

  // Floor painting state (mode "Sols")
  private floorRenderer: FloorRenderer | null = null;
  private floorData = new Map<FloorLayerName, FloorLayerData>();
  private floorLayerName: FloorLayerName = 'floor';
  private floorTool: 'brush' | 'brush3' | 'rect' | 'fill' | 'eraser' = 'brush';
  private floorValue: string | null = null;
  private isPaintingFloor = false;
  /** Modifications du geste en cours (clé "x,y"), envoyées d'un bloc au relâcher. */
  private floorStroke = new Map<string, FloorEdit>();
  private floorRectStart: { x: number; y: number } | null = null;
  private lastFloorCell: { x: number; y: number } | null = null;
  private floorPreview: Phaser.GameObjects.Graphics | null = null;
  private mapTilesW = 0;
  private mapTilesH = 0;

  // Text labels (mode "Texte") — liste tenue par React (EditorPage), rendue ici.
  private labelRenderer: LabelRenderer | null = null;
  /** Glisser d'étiquette : case de départ + décalage pointeur → case de l'étiquette (une étiquette longue couvre plusieurs cases). */
  private draggingLabel: { id: string; startX: number; startY: number; offsetX: number; offsetY: number; tileX: number; tileY: number } | null = null;
  private isPaintingWall = false;
  private lastPaintedWallTileX: number | null = null;
  private lastPaintedWallTileY: number | null = null;

  constructor() {
    super({ key: 'EditorScene' });
  }

  init(data: { zoneConfig: ZoneConfig; urls: ZoneAssetUrls }) {
    console.log('[EditorScene] init()', data);
    this.zoneConfig = data.zoneConfig;
    this.zoneUrls = data.urls;
    this.currentMode = 'select';
    this.selectedFurnitureId = null;
    this.selectedFurnitureUrl = null;
    this.selectedFurnitureLayer = 'furniture';
    this.selectedFurnitureIsSolid = false;
    this.ghostSprite = null;
    this.placedFurniture = [];
    this.selectedPlaced = null;
    this.selectionOutline = null;
    this.clickStart = null;
    this.selectedZoneType = null;
    this.isDrawingZone = false;
    this.zoneDrawPreview = null;
    this.placedZones = [];
    this.selectedZoneId = null;
    this.selectedWallStyleId = null;
    this.wallGhost = null;
    this.placedWalls = [];
    this.wallRenderer = null;
    this.pendingWallTiles.clear();
    this.selectedDoorId = null;
    this.doorGhost = null;
    this.floorRenderer = null;
    this.floorData = new Map();
    this.floorStroke = new Map();
    this.isPaintingFloor = false;
    this.floorRectStart = null;
    this.floorPreview = null;
    this.labelRenderer = null;
    this.draggingLabel = null;
    this.isPaintingWall = false;
    this.lastPaintedWallTileX = null;
    this.lastPaintedWallTileY = null;
  }

  preload() {
    console.log('[EditorScene] preload()');
    if (!this.zoneConfig || !this.zoneUrls) return;

    // Voir ZoneScene.preload() : requis pour que les textures cross-origin
    // (tilesets LimeZu depuis api.lumetic.fr) ne soient pas tainted pour WebGL.
    this.load.crossOrigin = 'anonymous';

    this.zoneLoader = new ZoneLoader(this);
    this.zoneLoader.loadZone(this.zoneConfig, this.zoneUrls, useGameStore.getState().jwt ?? '');
    this.zoneLoader.preloadFurniture();
    preloadWallAtlas(this);
    preloadFloorAtlas(this);

    // Sprites de portes — mêmes clés que ZoneScene (id du catalogue).
    for (const door of doorsCatalog as DoorCatalogEntry[]) {
      if (!this.textures.exists(door.id)) {
        this.load.spritesheet(door.id, door.url, { frameWidth: door.frameWidth, frameHeight: door.frameHeight });
      }
    }

    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      console.warn(`[EditorScene] Asset 404 — key="${file.key}" url="${file.src as string}"`);
    });
  }

  create() {
    console.log('[EditorScene] create()');
    if (!this.zoneConfig) return;

    const zoneId = this.zoneConfig.id;

    // ── Tilemap ──────────────────────────────────────────────
    const mapKey = `map:${zoneId}`;
    const map = this.make.tilemap({ key: mapKey });

    const tilesetObjects: Phaser.Tilemaps.Tileset[] = [];
    for (let i = 0; i < map.tilesets.length; i++) {
      const ts = map.tilesets[i];
      if (!ts) continue;
      const added = addExtrudedTilesetImage(this, map, ts, `tileset:${zoneId}:${i}`);
      if (added) tilesetObjects.push(added);
    }

    // ── Layers ───────────────────────────────────────────────
    // Mêmes profondeurs qu'en jeu (ZoneScene) : les calques de sol du TMJ
    // passent sous les sols peints dans l'éditeur, les autres au-dessus.
    for (const layerData of map.layers) {
      const layer = map.createLayer(layerData.name, tilesetObjects);
      if (!layer) continue;
      layer.setDepth(/^floor/i.test(layerData.name) ? FLOOR_DEPTHS.tmjFloor : FLOOR_DEPTHS.tmjOther);
    }

    this.mapWidth = map.widthInPixels;
    this.mapHeight = map.heightInPixels;
    this.mapTilesW = map.width;
    this.mapTilesH = map.height;

    // ── Grid overlay ─────────────────────────────────────────
    const grid = this.add.graphics();
    grid.lineStyle(1, 0xffffff, 0.08);
    for (let x = 0; x <= this.mapWidth; x += TILE) {
      grid.lineBetween(x, 0, x, this.mapHeight);
    }
    for (let y = 0; y <= this.mapHeight; y += TILE) {
      grid.lineBetween(0, y, this.mapWidth, y);
    }
    grid.setDepth(9999);
    grid.setScrollFactor(1);
    this.gridGraphics = grid;

    // ── Camera ───────────────────────────────────────────────
    // Arrivée centrée sur la carte. Le canvas (Scale.RESIZE) peut encore
    // changer de taille juste après l'ouverture (mise en page des panneaux) :
    // on recentre à chaque redimensionnement tant que l'admin n'a pas
    // touché à la vue.
    this.cameras.main.removeBounds();
    this.cameras.main.setZoom(1).centerOn(this.mapWidth / 2, this.mapHeight / 2);
    let viewTouched = false;
    const recenter = () => {
      if (!viewTouched) this.cameras.main.centerOn(this.mapWidth / 2, this.mapHeight / 2);
    };
    const markTouched = () => { viewTouched = true; };
    this.scale.on(Phaser.Scale.Events.RESIZE, recenter);
    this.input.once('pointerdown', markTouched);
    this.input.once('wheel', markTouched);
    const stopRecenter = () => this.scale.off(Phaser.Scale.Events.RESIZE, recenter);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, stopRecenter);
    this.events.once(Phaser.Scenes.Events.DESTROY, stopRecenter);

    // ── Input: Pan clic gauche + drag ────────────────────────
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (p.leftButtonDown()) {
        if (this.currentMode === 'furniture' && this.selectedFurnitureId) {
          this.placeFurnitureAtPointer(p);
          return;
        }
        if (this.currentMode === 'zone' && this.selectedZoneType) {
          this.startZoneDraw(p);
          return;
        }
        if (this.currentMode === 'zone' && !this.selectedZoneType) {
          this.trySelectZone(p);
          return;
        }
        if (this.currentMode === 'door' && this.selectedDoorId) {
          this.placeDoorAtPointer(p);
          return;
        }
        if (this.currentMode === 'floor' && (this.floorValue || this.floorTool === 'eraser')) {
          this.startFloorGesture(p);
          return;
        }
        if (this.currentMode === 'text') {
          this.startLabelGesture(p);
          return;
        }
        if (this.currentMode === 'slots' && this.startSlotGesture(p)) {
          return;
        }
        if (this.currentMode === 'select' && this.startDeskDrag(p)) {
          return;
        }
        if (this.currentMode === 'wall' && this.selectedWallStyleId) {
          this.isPaintingWall = true;
          this.lastPaintedWallTileX = null;
          this.lastPaintedWallTileY = null;
          this.placeWallAtPointer(p);
          return;
        }
        this.isDragging = true;
        this.clickStart = this.currentMode === 'select' || (this.currentMode === 'furniture' && !this.selectedFurnitureId)
          ? { x: p.x, y: p.y } : null;
        this.dragStartX = p.x + this.cameras.main.scrollX;
        this.dragStartY = p.y + this.cameras.main.scrollY;
        this.input.setDefaultCursor('grabbing');
      }
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      // Filet de sécurité : plus aucun bouton enfoncé alors qu'un geste est en
      // cours = relâchement jamais reçu (fenêtre ouverte au clic, alt-tab…).
      // Termine le geste au lieu de laisser la carte ou la zone collée à la souris.
      if (p.buttons === 0 && this.hasPointerGesture()) {
        endGesture(p);
        return;
      }
      if (this.slotDrag) {
        this.updateSlotDrag(p);
        return;
      }
      if (this.isDrawingZone) {
        this.updateZoneDrawPreview(p);
        return;
      }
      if (this.draggingZoneId) {
        this.updateZoneDragPreview(p);
        return;
      }
      if (this.isPaintingWall) {
        this.placeWallAtPointer(p);
        return;
      }
      if (this.draggingLabel) {
        const { x, y } = this.pointerTile(p);
        const d = this.draggingLabel;
        d.tileX = x + d.offsetX;
        d.tileY = y + d.offsetY;
        this.labelRenderer?.moveTo(d.id, d.tileX, d.tileY);
        return;
      }
      if (this.currentMode === 'floor') this.updateFloorPreview(p);
      if (this.isPaintingFloor) {
        this.continueFloorGesture(p);
        return;
      }
      if (this.isDragging) {
        this.cameras.main.scrollX = this.dragStartX - p.x;
        this.cameras.main.scrollY = this.dragStartY - p.y;
      }
      this.updateGhostPosition(p);
      this.updateWallGhostPosition(p);
      this.updateDoorGhostPosition(p);
    });

    // Relâché au-dessus d'une fenêtre ou d'un panneau (DOM par-dessus le
    // canvas, ex. la fenêtre d'une zone ouverte au clic) : Phaser émet
    // 'pointerupoutside', pas 'pointerup' — les deux terminent le geste.
    const endGesture = (p: Phaser.Input.Pointer) => {
      if (this.slotDrag) {
        this.finishSlotDrag();
        return;
      }
      if (this.isDrawingZone) {
        this.finishZoneDraw(p);
        return;
      }
      if (this.draggingZoneId) {
        this.finishZoneDrag();
        return;
      }
      if (this.isPaintingWall) {
        this.isPaintingWall = false;
        this.lastPaintedWallTileX = null;
        this.lastPaintedWallTileY = null;
        return;
      }
      if (this.isPaintingFloor) {
        this.finishFloorGesture(p);
        return;
      }
      if (this.draggingLabel) {
        const d = this.draggingLabel;
        this.draggingLabel = null;
        this.labelRenderer?.setAlpha(d.id, 1);
        if (d.tileX === d.startX && d.tileY === d.startY) {
          EventBus.emit('editor:label-click', { tileX: d.tileX, tileY: d.tileY, id: d.id });
        } else {
          EventBus.emit('editor:label-moved', { id: d.id, tileX: d.tileX, tileY: d.tileY });
        }
        return;
      }
      if (this.isDragging) {
        this.isDragging = false;
        this.input.setDefaultCursor('default');
        // Clic sans glisser : sélectionne le meuble posé sous le pointeur.
        const c = this.clickStart;
        this.clickStart = null;
        if (c && Math.hypot(p.x - c.x, p.y - c.y) < 5) this.selectPlacedAt(p);
      }
    };
    this.input.on('pointerup', endGesture);
    this.input.on('pointerupoutside', endGesture);

    // ── Clic droit: supprimer meuble (DOM natif — Phaser ne capture pas le bouton droit)
    this.game.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      if (this.currentMode !== 'furniture' && this.currentMode !== 'wall' && this.currentMode !== 'door' && this.currentMode !== 'floor' && this.currentMode !== 'text' && this.currentMode !== 'slots') return;
      const rect = this.game.canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const { x: worldX, y: worldY } = this.cameras.main.getWorldPoint(screenX, screenY);
      const tileX = Math.floor(worldX / TILE);
      const tileY = Math.floor(worldY / TILE);
      console.log('[EditorScene] right click screen:', screenX, screenY, 'world:', worldX, worldY, 'tile:', tileX, tileY);
      if (this.currentMode === 'wall') {
        this.removeWallAtTile(tileX, tileY);
        return;
      }
      if (this.currentMode === 'slots') {
        // Clic droit sur un emplacement = le retirer (refusé par l'API s'il est pris).
        const slot = this.slotAtTile(tileX, tileY);
        if (slot) EventBus.emit('editor:slot-remove', { id: slot.id });
        return;
      }
      if (this.currentMode === 'door') {
        const door = this.findDoorZoneAtTile(tileX, tileY);
        if (door) EventBus.emit('editor:door-remove', { id: door.dbId });
        return;
      }
      if (this.currentMode === 'text') {
        // Clic droit sur une étiquette = sa fenêtre (modifier / supprimer).
        const id = this.labelRenderer?.labelAt(worldX, worldY) ?? null;
        if (id) EventBus.emit('editor:label-click', { tileX, tileY, id });
        return;
      }
      if (this.currentMode === 'floor') {
        // Clic droit = gomme d'une case, quel que soit l'outil choisi.
        this.paintFloorCells([{ x: tileX, y: tileY }], null);
        this.commitFloorStroke();
        return;
      }
      console.log('[EditorScene] placed furniture:', this.placedFurniture.map(f => `${f.furnitureId}@(${f.tileX},${f.tileY})`));
      this.removeFurnitureAtTile(tileX, tileY);
    });

    // ── Input: Zoom molette (DOM direct) ─────────────────────
    const canvas = this.game.canvas;
    const detachWheelZoom = attachWheelZoom(canvas, {
      getZoom: () => this.cameras.main.zoom,
      setZoom: (level) => this.cameras.main.setZoom(level),
      min: 0.5,
      max: 2,
    });

    // ── Input: Zoom tactile pinch ────────────────────────────
    let touchStartDistance = 0;
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0]!.clientX - e.touches[1]!.clientX;
        const dy = e.touches[0]!.clientY - e.touches[1]!.clientY;
        touchStartDistance = Math.sqrt(dx * dx + dy * dy);
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const dx = e.touches[0]!.clientX - e.touches[1]!.clientX;
        const dy = e.touches[0]!.clientY - e.touches[1]!.clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (touchStartDistance > 0) {
          const newZoom = Phaser.Math.Clamp(
            this.cameras.main.zoom * (dist / touchStartDistance), 0.5, 2,
          );
          this.cameras.main.setZoom(newZoom);
          touchStartDistance = dist;
        }
      }
    };
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });

    // NOTE : Phaser n'appelle jamais automatiquement une méthode nommée
    // "shutdown" (contrairement à init/preload/create/update) — sans cet
    // enregistrement explicite, this.shutdown() ci-dessous ne se déclenche
    // jamais et les listeners EventBus de cette scène restent actifs pour
    // toujours après sa destruction, provoquant des crashs sur des scènes
    // mortes ("this.add" injecté sur l'objet Scene mais displayList/sys nuls).
    //
    // IMPORTANT : `game.destroy(true)` (voir EditorPage.loadZone(), appelé à
    // chaque changement de carte) émet l'event Scene Systems "destroy", PAS
    // "shutdown" — "shutdown" est réservé à un arrêt réversible (scene.stop()),
    // jamais utilisé ici (voir Systems.js de Phaser : shutdown()/destroy() sont
    // deux méthodes indépendantes, seule destroy() est appelée lors du teardown
    // complet du Game). On écoute les deux pour couvrir aussi un futur
    // scene.stop() éventuel, avec une garde pour éviter un double-nettoyage.
    const onSceneTeardown = () => {
      if (this.hasShutdown) return;
      this.hasShutdown = true;
      detachWheelZoom();
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      this.shutdown();
    };
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, onSceneTeardown);
    this.events.once(Phaser.Scenes.Events.DESTROY, onSceneTeardown);

    // ── EventBus listeners ───────────────────────────────────
    EventBus.on('editor:set-mode', ({ mode }: { mode: 'select' | 'furniture' | 'zone' | 'wall' | 'door' | 'floor' | 'text' | 'slots' }) => {
      this.currentMode = mode;
      if (mode !== 'slots') this.cancelSlotDrag();
      this.renderDeskSlots(); // contour plus marqué en mode « Emplacements »
      if (mode !== 'select' && mode !== 'furniture') this.setSelectedPlaced(null);
      if (mode !== 'furniture') {
        this.clearGhost();
        this.selectedFurnitureId = null;
        this.selectedFurnitureUrl = null;
      }
      if (mode !== 'wall') {
        this.clearWallGhost();
        this.selectedWallStyleId = null;
        this.isPaintingWall = false;
      }
      if (mode !== 'door') {
        this.clearDoorGhost();
        this.selectedDoorId = null;
      }
      if (mode !== 'floor') {
        this.floorPreview?.clear();
        this.floorValue = null;
        this.isPaintingFloor = false;
        this.floorRectStart = null;
      }
    });

    EventBus.on('editor:personal-desks', ({ desks }) => this.renderPersonalDesks(desks));
    EventBus.on('editor:desk-slots', ({ slots }) => {
      this.deskSlots = slots;
      this.renderDeskSlots();
    });

    EventBus.on('editor:labels-updated', ({ labels }) => {
      this.labelRenderer ??= new LabelRenderer(this);
      this.labelRenderer.render(labels);
    });

    EventBus.on('editor:floor-settings', ({ layer, tool, value }) => {
      this.floorLayerName = layer;
      this.floorTool = tool;
      this.floorValue = value;
    });

    EventBus.on('editor:zoom', ({ direction }: { direction: 'in' | 'out' }) => {
      const delta = direction === 'in' ? 0.25 : -0.25;
      const newZoom = Phaser.Math.Clamp(this.cameras.main.zoom + delta, 0.5, 2);
      this.cameras.main.setZoom(newZoom);
    });

    EventBus.on('editor:select-furniture', (data: { id: string; url: string; layer: string; isSolid: boolean; solidBaseOnly: boolean } | null) => {
      if (!data) {
        this.selectedFurnitureId = null;
        this.selectedFurnitureUrl = null;
        this.selectedFurnitureLayer = 'furniture';
        this.selectedFurnitureIsSolid = false;
        this.clearGhost();
        return;
      }
      this.selectedFurnitureId = data.id;
      this.selectedFurnitureUrl = data.url;
      this.selectedFurnitureLayer = data.layer;
      this.selectedFurnitureIsSolid = data.isSolid;
      this.selectedFurnitureSolidBaseOnly = data.solidBaseOnly;
      this.setSelectedPlaced(null);
      this.createGhost(data.id);
    });

    EventBus.on('editor:rotate-placed-furniture', (data) => {
      const item = this.selectedPlaced;
      if (!item) return;
      const next = 'step' in data ? nextOrientation(item.furnitureId, data.step) : data.furnitureId;
      if (next !== item.furnitureId) this.changePlacedFurniture(item, next);
    });

    EventBus.on('editor:select-zone-type', (data: { type: ZoneType } | null) => {
      this.selectedZoneType = data?.type ?? null;
      this.clearSelectedZoneHighlight();
    });

    EventBus.on('editor:zone-created', (data) => {
      this.spawnZoneRect(data.id, data.type as ZoneType, data.name, data.properties as Record<string, unknown>, data.x, data.y, data.width, data.height);
    });

    EventBus.on('editor:zone-updated', (data) => {
      this.removeZoneRect(data.id);
      this.spawnZoneRect(data.id, data.type as ZoneType, data.name, data.properties as Record<string, unknown>, data.x, data.y, data.width, data.height);
    });

    EventBus.on('editor:zone-deleted', ({ id }) => {
      this.removeZoneRect(id);
    });

    // Glisser-déposer rejeté côté serveur (chevauchement) — revert visuel,
    // le déplacement local était optimiste pendant le drag.
    EventBus.on('editor:zone-move-rejected', (data: { id: string; x: number; y: number }) => {
      const zone = this.placedZones.find((z) => z.dbId === data.id);
      if (!zone) return;
      zone.x = data.x;
      zone.y = data.y;
      this.redrawZoneGraphics(zone);
      if (this.selectedZoneId === zone.dbId) this.drawSelectedHighlight(zone);
      zone.label.setPosition(zone.x * TILE + 4, zone.y * TILE + 2);
    });

    EventBus.on('editor:toggle-grid', ({ visible }: { visible: boolean }) => {
      this.gridGraphics?.setVisible(visible);
    });

    EventBus.on('editor:select-door', (data: { id: string } | null) => {
      this.selectedDoorId = data?.id ?? null;
      this.createDoorGhost();
    });

    // Zone porte créée via l'API : la porte remplace le mur sous elle.
    EventBus.on('editor:door-placed', ({ x, y, width }: { x: number; y: number; width: number }) => {
      for (let k = 0; k < width; k++) this.removeWallAtTile(x + k, y);
    });

    EventBus.on('editor:select-wall-style', (data: { id: string } | null) => {
      if (!data) {
        this.selectedWallStyleId = null;
        this.clearWallGhost();
        return;
      }
      this.selectedWallStyleId = data.id;
      this.createWallGhost(data.id);
    });

    // ── Debug ────────────────────────────────────────────────
    if (import.meta.env.DEV) {
      (window as any).__EDITOR_SCENE__ = this;
      (window as any).__EDITOR_GAME__ = this.game;
      (canvas as any).__phaser_scene__ = this;
    }
    console.log('[EditorScene] exposed on window.__EDITOR_SCENE__');

    EventBus.emit('editor:scene-ready', {});
    console.log(`[EditorScene] ready — map ${this.mapWidth}x${this.mapHeight}`);

    // ── ME-2e: Load existing furniture from DB ───────────────
    this.loadFurnitureFromDB();

    // ── ME-3f: Load existing zones from DB ─────────────────
    this.loadZonesFromDB();

    // ── Load existing walls from DB ────────────────────────
    this.loadWallsFromDB();

    // ── Load painted floors from DB ────────────────────────
    this.loadFloorsFromDB();
  }

  // ── Ghost sprite (ME-2d) ─────────────────────────────────

  private createGhost(furnitureId: string): void {
    const prev = this.ghostSprite ? { x: this.ghostSprite.x, y: this.ghostSprite.y, visible: this.ghostSprite.visible } : null;
    this.clearGhost();
    const entry = findFurniture(furnitureId);
    if (!entry) return;
    if (!hasFurnitureTexture(this, entry)) {
      // Tampon de planche : texture découpée à la première sélection.
      void ensureFurnitureTextures(this, [entry]).then(() => {
        if (this.selectedFurnitureId === furnitureId && !this.ghostSprite) this.createGhost(furnitureId);
      });
      return;
    }
    this.ghostSprite = spawnFurnitureImage(this, entry, 0, 0);
    this.ghostSprite.setAlpha(0.5);
    this.ghostSprite.setDepth(10000);
    // Nouvelle vue du même objet (touche R) : le fantôme reste sous le pointeur.
    if (prev) this.ghostSprite.setPosition(prev.x, prev.y);
    this.ghostSprite.setVisible(prev?.visible ?? false);
  }

  private clearGhost(): void {
    this.ghostSprite?.destroy();
    this.ghostSprite = null;
  }

  private pointerToWorld(p: Phaser.Input.Pointer): { worldX: number; worldY: number } {
    // p.worldX/worldY sont maintenus par Phaser via camera.getWorldPoint() — le zoom
    // pivote autour du centre de la caméra, pas du coin haut-gauche, donc un calcul
    // manuel (screenX / zoom + scrollX) dérive dès que zoom !== 1.
    return { worldX: p.worldX, worldY: p.worldY };
  }

  private updateGhostPosition(p: Phaser.Input.Pointer): void {
    if (!this.ghostSprite || this.currentMode !== 'furniture') return;
    const { worldX, worldY } = this.pointerToWorld(p);
    const tileX = Math.floor(worldX / TILE);
    const tileY = Math.floor(worldY / TILE);
    const snapX = tileX * TILE;
    const snapY = (tileY + 1) * TILE;
    this.ghostSprite.setPosition(snapX, snapY);
    this.ghostSprite.setVisible(true);
  }

  // ── Place furniture (ME-2d) ──────────────────────────────

  private placeFurnitureAtPointer(p: Phaser.Input.Pointer): void {
    if (!this.selectedFurnitureId || !this.zoneConfig) return;
    const { worldX, worldY } = this.pointerToWorld(p);
    const tileX = Math.floor(worldX / TILE);
    const tileY = Math.floor(worldY / TILE);
    console.log('[EditorScene] placed at tile:', tileX, tileY, 'world:', worldX, worldY);
    const furnitureId = this.selectedFurnitureId;
    const mapId = this.zoneConfig.slug;
    const entry = findFurniture(furnitureId);
    // Objet debout : base bloquante et tri en Y imposés (standingPlacement).
    const placement = standingPlacement(entry, {
      layer: this.selectedFurnitureLayer,
      isSolid: this.selectedFurnitureIsSolid,
      collisionGrid: entry && this.selectedFurnitureIsSolid && this.selectedFurnitureSolidBaseOnly
        ? baseOnlyCollisionGrid(Math.round(entry.dimensions.w / TILE), Math.round(entry.dimensions.h / TILE))
        : undefined,
    });

    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/furniture`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        furnitureId, tileX, tileY, ...placement,
      }),
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((item: { id: string; furnitureId: string; tileX: number; tileY: number; layer: string }) => {
        this.spawnFurnitureSprite(item.id, item.furnitureId, item.tileX, item.tileY, item.layer);
        EventBus.emit('editor:furniture-placed', {
          id: item.id, furnitureId: item.furnitureId,
          tileX: item.tileX, tileY: item.tileY,
        });
        console.log(`[EditorScene] furniture placed: ${item.furnitureId} at (${item.tileX},${item.tileY})`);
      })
      .catch((err) => console.error('[EditorScene] place furniture failed:', err));
  }

  // ── Remove furniture (ME-2d) ─────────────────────────────

  private removeFurnitureAtTile(tileX: number, tileY: number): void {
    if (!this.zoneConfig) return;

    const idx = this.placedFurniture.findIndex((f) => f.tileX === tileX && f.tileY === tileY);
    if (idx === -1) return;

    const item = this.placedFurniture[idx]!;
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();

    item.sprite.destroy();
    this.placedFurniture.splice(idx, 1);
    if (this.selectedPlaced === item) this.setSelectedPlaced(null);

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/furniture/${encodeURIComponent(item.dbId)}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
      },
    })
      .then(() => {
        EventBus.emit('editor:furniture-removed', { id: item.dbId });
        console.log(`[EditorScene] furniture removed: ${item.dbId}`);
      })
      .catch((err) => console.error('[EditorScene] remove furniture failed:', err));
  }

  // ── Spawn sprite helper ──────────────────────────────────

  private spawnFurnitureSprite(dbId: string, furnitureId: string, tileX: number, tileY: number, layer = 'furniture', onSpawned?: (item: PlacedFurniture) => void): void {
    // furnitureId peut être un ancien ID séquentiel — la texture est indexée
    // par l'ID stable (voir findFurniture()).
    const entry = findFurniture(furnitureId);
    if (!entry) {
      console.warn(`[EditorScene] furniture not in catalog: ${furnitureId}`);
      return;
    }
    if (!hasFurnitureTexture(this, entry)) {
      // Entrée masquée de la palette (pas préchargée) ou tampon de planche.
      void ensureFurnitureTextures(this, [entry]).then(() => {
        if (hasFurnitureTexture(this, entry)) this.spawnFurnitureSprite(dbId, furnitureId, tileX, tileY, layer, onSpawned);
      });
      return;
    }
    const px = tileX * TILE;
    const bottomY = (tileY + 1) * TILE;
    const sprite = spawnFurnitureImage(this, entry, px, bottomY);
    if (layer === 'furnitureTop') {
      sprite.setDepth(999);
    } else if ((layer === 'floor' && !entry.standing) || entry.walkable) {
      // Pont, ponton : toujours sous les avatars, même posé en « Mobilier ».
      // Objet debout : jamais au sol (standingPlacement).
      sprite.setDepth(FLOOR_DEPTHS.floorObjects);
    } else {
      sprite.setDepth(100 + furnitureSortY(entry, bottomY) / 10 + 0.001); // devant le mur de la même rangée, comme en jeu
    }
    const item: PlacedFurniture = { dbId, furnitureId, tileX, tileY, layer, sprite };
    this.placedFurniture.push(item);
    onSpawned?.(item);
  }

  // ── Vue d'un meuble posé (face, dos, profils) ────────────

  /** Meuble posé sous le pointeur : le plus en avant de ceux dont l'image le couvre. */
  private selectPlacedAt(p: Phaser.Input.Pointer): void {
    const { worldX, worldY } = this.pointerToWorld(p);
    let hit: PlacedFurniture | null = null;
    for (const f of this.placedFurniture) {
      if (!f.sprite.getBounds().contains(worldX, worldY)) continue;
      if (!hit || f.sprite.depth >= hit.sprite.depth) hit = f;
    }
    this.setSelectedPlaced(hit);
  }

  private setSelectedPlaced(item: PlacedFurniture | null): void {
    if (this.selectedPlaced === item && (item === null || this.selectionOutline)) return;
    this.selectedPlaced = item;
    this.selectionOutline?.destroy();
    this.selectionOutline = null;
    if (item) {
      const b = item.sprite.getBounds();
      this.selectionOutline = this.add.graphics().setDepth(9999)
        .lineStyle(2, 0x4F6EF7, 1).strokeRect(b.x - 1, b.y - 1, b.width + 2, b.height + 2);
    }
    EventBus.emit('editor:placed-furniture-selected', item ? { id: item.dbId, furnitureId: item.furnitureId } : null);
  }

  /** Change l'image d'un meuble posé (autre vue), même case : ancre bas-gauche conservée. */
  private changePlacedFurniture(item: PlacedFurniture, furnitureId: string): void {
    if (!this.zoneConfig) return;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const mapId = this.zoneConfig.slug;
    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/furniture/${encodeURIComponent(item.dbId)}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': getTenantSlug(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ furnitureId }),
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((updated: { furnitureId: string }) => {
        const idx = this.placedFurniture.indexOf(item);
        if (idx === -1) return; // retiré entre-temps
        const wasSelected = this.selectedPlaced === item;
        item.sprite.destroy();
        this.placedFurniture.splice(idx, 1);
        this.spawnFurnitureSprite(item.dbId, updated.furnitureId, item.tileX, item.tileY, item.layer, (spawned) => {
          if (!wasSelected) return;
          this.selectedPlaced = null;
          this.setSelectedPlaced(spawned);
        });
      })
      .catch((err) => console.error('[EditorScene] change furniture view failed:', err));
  }

  // ── ME-2e: Load from DB ──────────────────────────────────

  private loadFurnitureFromDB(): void {
    if (!this.zoneConfig) return;
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/furniture`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
      },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((items: Array<{ id: string; furnitureId: string; tileX: number; tileY: number; layer: string }>) => {
        for (const item of items) {
          this.spawnFurnitureSprite(item.id, item.furnitureId, item.tileX, item.tileY, item.layer);
        }
        console.log(`[EditorScene] loaded ${items.length} furniture from DB`);
      })
      .catch((err) => console.warn('[EditorScene] load furniture failed:', err));
  }

  // ── Wall ghost ────────────────────────────────────────────
  // Aperçu d'un pan isolé du style choisi : bas de face sur la case visée,
  // haut de face sur la case du dessus (même règle que computeWallTiles).

  private createWallGhost(wallStyleId: string): void {
    this.clearWallGhost();
    const style = findWallStyle(wallStyleId);
    if (!style) return;
    const { upper, lower } = wallPreviewFrames(style);
    const parts = [this.add.image(0, 0, WALL_ATLAS_KEY, lower).setOrigin(0, 0)];
    if (upper !== null) parts.push(this.add.image(0, -TILE, WALL_ATLAS_KEY, upper).setOrigin(0, 0));
    this.wallGhost = this.add.container(0, 0, parts);
    this.wallGhost.setAlpha(0.6);
    this.wallGhost.setDepth(10000);
    this.wallGhost.setVisible(false);
  }

  private clearWallGhost(): void {
    this.wallGhost?.destroy();
    this.wallGhost = null;
  }

  private updateWallGhostPosition(p: Phaser.Input.Pointer): void {
    if (!this.wallGhost || this.currentMode !== 'wall') return;
    const { worldX, worldY } = this.pointerToWorld(p);
    const tileX = Math.floor(worldX / TILE);
    const tileY = Math.floor(worldY / TILE);
    this.wallGhost.setPosition(tileX * TILE, tileY * TILE);
    this.wallGhost.setVisible(true);
  }

  // ── Place wall ────────────────────────────────────────────

  private placeWallAtPointer(p: Phaser.Input.Pointer): void {
    if (!this.selectedWallStyleId || !this.zoneConfig) return;
    const { worldX, worldY } = this.pointerToWorld(p);
    const tileX = Math.floor(worldX / TILE);
    const tileY = Math.floor(worldY / TILE);

    // Peinture continue au drag : ne pas re-poser sur la même tuile tant
    // que le curseur ne l'a pas quittée, et ignorer une tuile déjà occupée
    // ou en cours d'enregistrement.
    const tileKey = `${tileX},${tileY}`;
    if (tileX === this.lastPaintedWallTileX && tileY === this.lastPaintedWallTileY) return;
    if (this.placedWalls.some((w) => w.tileX === tileX && w.tileY === tileY)) return;
    if (this.pendingWallTiles.has(tileKey)) return;
    this.lastPaintedWallTileX = tileX;
    this.lastPaintedWallTileY = tileY;

    const wallStyleId = this.selectedWallStyleId;
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();

    this.pendingWallTiles.add(tileKey);
    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/walls`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tileX, tileY, wallStyleId }),
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((item: { id: string; tileX: number; tileY: number; wallStyleId: string }) => {
        this.placedWalls.push({ dbId: item.id, tileX: item.tileX, tileY: item.tileY, wallStyleId: item.wallStyleId });
        this.renderWalls();
        EventBus.emit('editor:wall-placed', {
          id: item.id, tileX: item.tileX, tileY: item.tileY, wallStyleId: item.wallStyleId,
        });
        console.log(`[EditorScene] wall placed: ${item.wallStyleId} at (${item.tileX},${item.tileY})`);
      })
      .catch((err) => console.error('[EditorScene] place wall failed:', err))
      .finally(() => this.pendingWallTiles.delete(tileKey));
  }

  // ── Remove wall ───────────────────────────────────────────

  private removeWallAtTile(tileX: number, tileY: number): void {
    if (!this.zoneConfig) return;

    const idx = this.placedWalls.findIndex((w) => w.tileX === tileX && w.tileY === tileY);
    if (idx === -1) return;

    const item = this.placedWalls[idx]!;
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();

    this.placedWalls.splice(idx, 1);
    this.renderWalls();

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/walls/${encodeURIComponent(item.dbId)}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
      },
    })
      .then(() => {
        EventBus.emit('editor:wall-removed', { id: item.dbId });
        console.log(`[EditorScene] wall removed: ${item.dbId}`);
      })
      .catch((err) => console.error('[EditorScene] remove wall failed:', err));
  }

  // ── Wall rendering ──────────────────────────────────────
  // L'apparence d'un mur dépend de ses voisins : tout est redessiné à chaque
  // ajout/retrait (voir WallRenderer). Même tri en Y que le mobilier posé.

  private renderWalls(): void {
    this.wallRenderer ??= new WallRenderer(this, (bottomY) => 100 + bottomY / 10);
    this.wallRenderer.render(this.placedWalls);
  }

  // ── Load walls from DB ──────────────────────────────────

  private loadWallsFromDB(): void {
    if (!this.zoneConfig) return;
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/walls`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
      },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((items: Array<{ id: string; tileX: number; tileY: number; wallStyleId: string }>) => {
        this.placedWalls = items.map((item) => ({
          dbId: item.id, tileX: item.tileX, tileY: item.tileY, wallStyleId: item.wallStyleId,
        }));
        this.renderWalls();
        console.log(`[EditorScene] loaded ${items.length} walls from DB`);
      })
      .catch((err) => console.warn('[EditorScene] load walls failed:', err));
  }

  // ── Floors (mode "Sols") ─────────────────────────────────
  // Chaque geste (coup de pinceau, rectangle, remplissage) est appliqué et
  // redessiné localement tout de suite, puis envoyé en UNE requête au
  // relâcher (PATCH /floors/:layer) — même fonction applyFloorEdits() que
  // l'API, donc même résultat des deux côtés.

  private floorLayerData(): FloorLayerData {
    let data = this.floorData.get(this.floorLayerName);
    if (!data) {
      data = emptyFloorLayer(this.mapTilesW, this.mapTilesH);
      this.floorData.set(this.floorLayerName, data);
    }
    return data;
  }

  private pointerTile(p: Phaser.Input.Pointer): { x: number; y: number } {
    const { worldX, worldY } = this.pointerToWorld(p);
    return { x: Math.floor(worldX / TILE), y: Math.floor(worldY / TILE) };
  }

  private inMap(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.mapTilesW && y < this.mapTilesH;
  }

  private brushCells(c: { x: number; y: number }): Array<{ x: number; y: number }> {
    if (this.floorTool !== 'brush3') return [c];
    const cells = [];
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) cells.push({ x: c.x + dx, y: c.y + dy });
    return cells;
  }

  private paintFloorCells(cells: Array<{ x: number; y: number }>, value: string | null): void {
    const inside = cells.filter((c) => this.inMap(c.x, c.y));
    if (inside.length === 0) return;
    const edits = inside.map((c) => ({ x: c.x, y: c.y, value }));
    const data = applyFloorEdits(this.floorLayerData(), edits, this.mapTilesW, this.mapTilesH);
    this.floorData.set(this.floorLayerName, data);
    for (const e of edits) this.floorStroke.set(`${e.x},${e.y}`, e);
    this.floorRenderer ??= new FloorRenderer(this, { floor: FLOOR_DEPTHS.floor, overlay: FLOOR_DEPTHS.overlay });
    this.floorRenderer.renderCells(this.floorLayerName, data, inside);
  }

  private startFloorGesture(p: Phaser.Input.Pointer): void {
    const c = this.pointerTile(p);
    if (this.floorTool === 'fill') {
      this.paintFloorCells(this.floodFillCells(c), this.floorValue);
      this.commitFloorStroke();
      return;
    }
    this.isPaintingFloor = true;
    this.lastFloorCell = c;
    if (this.floorTool === 'rect') {
      this.floorRectStart = c;
      this.updateFloorPreview(p);
      return;
    }
    this.paintFloorCells(this.brushCells(c), this.floorTool === 'eraser' ? null : this.floorValue);
  }

  private continueFloorGesture(p: Phaser.Input.Pointer): void {
    if (this.floorTool === 'rect') return; // aperçu seul, voir updateFloorPreview
    // Souris rapide : les pointermove sautent des cases — on trace la ligne
    // depuis la dernière case peinte.
    const to = this.pointerTile(p);
    const from = this.lastFloorCell ?? to;
    if (from.x === to.x && from.y === to.y && this.lastFloorCell) return;
    const steps = Math.max(Math.abs(to.x - from.x), Math.abs(to.y - from.y), 1);
    const cells = [];
    for (let i = 1; i <= steps; i++) {
      const c = { x: Math.round(from.x + ((to.x - from.x) * i) / steps), y: Math.round(from.y + ((to.y - from.y) * i) / steps) };
      cells.push(...this.brushCells(c));
    }
    this.lastFloorCell = to;
    this.paintFloorCells(cells, this.floorTool === 'eraser' ? null : this.floorValue);
  }

  private finishFloorGesture(p: Phaser.Input.Pointer): void {
    this.isPaintingFloor = false;
    this.lastFloorCell = null;
    if (this.floorTool === 'rect' && this.floorRectStart) {
      const end = this.pointerTile(p);
      const cells = [];
      for (let y = Math.min(this.floorRectStart.y, end.y); y <= Math.max(this.floorRectStart.y, end.y); y++) {
        for (let x = Math.min(this.floorRectStart.x, end.x); x <= Math.max(this.floorRectStart.x, end.x); x++) cells.push({ x, y });
      }
      this.floorRectStart = null;
      this.paintFloorCells(cells, this.floorValue);
      this.updateFloorPreview(p);
    }
    this.commitFloorStroke();
  }

  /** Cases contiguës (4 voisins) de même valeur que la case de départ, dans la carte. */
  private floodFillCells(start: { x: number; y: number }): Array<{ x: number; y: number }> {
    if (!this.inMap(start.x, start.y)) return [];
    const data = this.floorLayerData();
    const target = floorValueAt(data, start.x, start.y);
    if (target === this.floorValue) return [];
    const seen = new Set<number>([start.y * this.mapTilesW + start.x]);
    const queue = [start];
    const cells = [];
    while (queue.length > 0) {
      const c = queue.pop()!;
      cells.push(c);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const x = c.x + dx, y = c.y + dy;
        const key = y * this.mapTilesW + x;
        if (!this.inMap(x, y) || seen.has(key) || floorValueAt(data, x, y) !== target) continue;
        seen.add(key);
        queue.push({ x, y });
      }
    }
    return cells;
  }

  /** Contour des cases visées : pinceau (1 ou 3×3) ou rectangle en cours. */
  private updateFloorPreview(p: Phaser.Input.Pointer): void {
    this.floorPreview ??= this.add.graphics().setDepth(9998);
    const g = this.floorPreview;
    g.clear();
    if (this.currentMode !== 'floor') return;
    const c = this.pointerTile(p);
    let x0 = c.x, y0 = c.y, x1 = c.x, y1 = c.y;
    if (this.floorTool === 'brush3') { x0--; y0--; x1++; y1++; }
    if (this.floorTool === 'rect' && this.floorRectStart) {
      x0 = Math.min(this.floorRectStart.x, c.x); x1 = Math.max(this.floorRectStart.x, c.x);
      y0 = Math.min(this.floorRectStart.y, c.y); y1 = Math.max(this.floorRectStart.y, c.y);
      g.fillStyle(0xffffff, 0.15);
      g.fillRect(x0 * TILE, y0 * TILE, (x1 - x0 + 1) * TILE, (y1 - y0 + 1) * TILE);
    }
    g.lineStyle(2, this.floorTool === 'eraser' ? 0xf07070 : 0xffffff, 0.9);
    g.strokeRect(x0 * TILE, y0 * TILE, (x1 - x0 + 1) * TILE, (y1 - y0 + 1) * TILE);
  }

  private commitFloorStroke(): void {
    if (this.floorStroke.size === 0 || !this.zoneConfig) return;
    const edits = [...this.floorStroke.values()];
    const layer = this.floorLayerName;
    this.floorStroke = new Map();
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(this.zoneConfig.slug)}/floors/${layer}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': getTenantSlug(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ width: this.mapTilesW, height: this.mapTilesH, edits }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        EventBus.emit('editor:floor-painted', { layer, count: edits.length });
      })
      .catch((err) => {
        console.error('[EditorScene] save floor failed:', err);
        EventBus.emit('editor:toast', { message: 'Le sol n\'a pas pu être enregistré - rechargez la carte.' });
      });
  }

  private loadFloorsFromDB(): void {
    if (!this.zoneConfig) return;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(this.zoneConfig.slug)}/floors`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': getTenantSlug(),
      },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((layers: Array<FloorLayerData & { layer: FloorLayerName }>) => {
        this.floorRenderer ??= new FloorRenderer(this, { floor: FLOOR_DEPTHS.floor, overlay: FLOOR_DEPTHS.overlay });
        for (const l of layers) {
          const data: FloorLayerData = { width: l.width, height: l.height, palette: l.palette, cells: l.cells };
          this.floorData.set(l.layer, data);
          this.floorRenderer.render(l.layer, data);
        }
        console.log(`[EditorScene] loaded ${layers.length} floor layers from DB`);
      })
      .catch((err) => console.warn('[EditorScene] load floors failed:', err));
  }

  // ── Text labels (mode "Texte") ────────────────────────────
  // Clic sur une case vide → nouvelle étiquette ; clic sur une étiquette →
  // la modifier ; glisser une étiquette → la déplacer. Les fenêtres et les
  // appels API sont côté React (EditorPage), comme pour les portes.

  private startLabelGesture(p: Phaser.Input.Pointer): void {
    const { worldX, worldY } = this.pointerToWorld(p);
    const { x, y } = this.pointerTile(p);
    const id = this.labelRenderer?.labelAt(worldX, worldY) ?? null;
    if (!id) {
      EventBus.emit('editor:label-click', { tileX: x, tileY: y, id: null });
      return;
    }
    const tile = this.labelRenderer!.tileOf(id)!;
    this.draggingLabel = {
      id, startX: tile.tileX, startY: tile.tileY,
      offsetX: tile.tileX - x, offsetY: tile.tileY - y,
      tileX: tile.tileX, tileY: tile.tileY,
    };
    this.labelRenderer?.setAlpha(id, 0.6);
  }

  // ── Doors (mode "Portes") ───────────────────────────────
  // Clic sur la rangée d'un mur horizontal (la rangée bloquante, celle qu'on
  // peint) : la porte s'y ancre par le bas et occupe frameWidth/32 cases.
  // La zone est créée côté React (EditorPage, qui gère les zones et leurs
  // erreurs API), qui renvoie 'editor:door-placed' pour découper le mur.

  private createDoorGhost(): void {
    this.clearDoorGhost();
    if (!this.selectedDoorId || !this.textures.exists(this.selectedDoorId)) return;
    this.doorGhost = this.add.image(0, 0, this.selectedDoorId, 0).setOrigin(0, 1);
    this.doorGhost.setAlpha(0.6);
    this.doorGhost.setDepth(10000);
    this.doorGhost.setVisible(false);
  }

  private clearDoorGhost(): void {
    this.doorGhost?.destroy();
    this.doorGhost = null;
  }

  private updateDoorGhostPosition(p: Phaser.Input.Pointer): void {
    if (!this.doorGhost || this.currentMode !== 'door') return;
    const { worldX, worldY } = this.pointerToWorld(p);
    this.doorGhost.setPosition(Math.floor(worldX / TILE) * TILE, (Math.floor(worldY / TILE) + 1) * TILE);
    this.doorGhost.setVisible(true);
  }

  private placeDoorAtPointer(p: Phaser.Input.Pointer): void {
    const door = this.selectedDoorId ? findDoor(this.selectedDoorId) : undefined;
    if (!door) return;
    const { worldX, worldY } = this.pointerToWorld(p);
    EventBus.emit('editor:door-place', {
      doorSprite: door.id,
      x: Math.floor(worldX / TILE),
      y: Math.floor(worldY / TILE),
      width: Math.max(1, Math.round(door.frameWidth / TILE)),
    });
  }

  private findDoorZoneAtTile(tileX: number, tileY: number): PlacedZone | undefined {
    return this.placedZones.find((z) =>
      z.type === 'door' &&
      tileX >= z.x && tileX < z.x + z.width &&
      tileY >= z.y && tileY < z.y + z.height,
    );
  }

  // ── Zone drawing (ME-3d) ────────────────────────────────

  private startZoneDraw(p: Phaser.Input.Pointer): void {
    const { worldX, worldY } = this.pointerToWorld(p);
    this.zoneDrawStartTileX = Math.floor(worldX / TILE);
    this.zoneDrawStartTileY = Math.floor(worldY / TILE);
    this.isDrawingZone = true;

    this.zoneDrawPreview?.destroy();
    this.zoneDrawPreview = this.add.graphics();
    this.zoneDrawPreview.setDepth(9998);
  }

  private updateZoneDrawPreview(p: Phaser.Input.Pointer): void {
    if (!this.zoneDrawPreview || !this.selectedZoneType) return;
    const { worldX, worldY } = this.pointerToWorld(p);
    const endTileX = Math.floor(worldX / TILE);
    const endTileY = Math.floor(worldY / TILE);

    const x = Math.min(this.zoneDrawStartTileX, endTileX) * TILE;
    const y = Math.min(this.zoneDrawStartTileY, endTileY) * TILE;
    const w = (Math.abs(endTileX - this.zoneDrawStartTileX) + 1) * TILE;
    const h = (Math.abs(endTileY - this.zoneDrawStartTileY) + 1) * TILE;

    const color = ZONE_COLORS[this.selectedZoneType];
    this.zoneDrawPreview.clear();
    this.zoneDrawPreview.fillStyle(color, 0.3);
    this.zoneDrawPreview.fillRect(x, y, w, h);
    this.zoneDrawPreview.lineStyle(2, color, 0.8);
    this.zoneDrawPreview.strokeRect(x, y, w, h);
  }

  private finishZoneDraw(p: Phaser.Input.Pointer): void {
    this.isDrawingZone = false;
    this.zoneDrawPreview?.destroy();
    this.zoneDrawPreview = null;

    if (!this.selectedZoneType) return;
    const { worldX, worldY } = this.pointerToWorld(p);
    const endTileX = Math.floor(worldX / TILE);
    const endTileY = Math.floor(worldY / TILE);

    const x = Math.min(this.zoneDrawStartTileX, endTileX);
    const y = Math.min(this.zoneDrawStartTileY, endTileY);
    const width = Math.abs(endTileX - this.zoneDrawStartTileX) + 1;
    const height = Math.abs(endTileY - this.zoneDrawStartTileY) + 1;

    if (width < 1 || height < 1) return;

    EventBus.emit('editor:zone-drawn', {
      type: this.selectedZoneType,
      x, y, width, height,
    });
  }

  /** Un geste à la souris (glisser, peindre, tracer) est en cours. */
  private hasPointerGesture(): boolean {
    return !!this.slotDrag || this.isDrawingZone || !!this.draggingZoneId || this.isPaintingWall
      || !!this.draggingLabel || this.isPaintingFloor || this.isDragging;
  }

  private trySelectZone(p: Phaser.Input.Pointer): void {
    const { worldX, worldY } = this.pointerToWorld(p);
    const tileX = Math.floor(worldX / TILE);
    const tileY = Math.floor(worldY / TILE);

    // Portes en premier : une porte peut chevaucher une bulle/salle de
    // réunion (voir zonesConflict côté API) — sans ça, cliquer sur la porte
    // sélectionnerait la bulle et la porte ne serait plus éditable.
    const byPriority = [...this.placedZones].sort((a, b) => Number(b.type === 'door') - Number(a.type === 'door'));
    for (const zone of byPriority) {
      if (tileX >= zone.x && tileX < zone.x + zone.width &&
          tileY >= zone.y && tileY < zone.y + zone.height) {
        this.clearSelectedZoneHighlight();
        this.selectedZoneId = zone.dbId;
        this.drawSelectedHighlight(zone);

        // Glisser potentiel — confirmé seulement si le pointeur bouge avant
        // pointerup (sinon c'est un simple clic de sélection, pas un drag).
        // La fenêtre de la zone ne s'ouvre qu'au relâchement (finishZoneDrag) :
        // ouverte dès l'appui, elle recouvrait la carte et le glisser n'arrivait
        // jamais jusqu'à la scène.
        this.draggingZoneId = zone.dbId;
        this.dragZoneStartTileX = tileX;
        this.dragZoneStartTileY = tileY;
        this.dragZoneOrigX = zone.x;
        this.dragZoneOrigY = zone.y;
        return;
      }
    }
    this.clearSelectedZoneHighlight();
  }

  private updateZoneDragPreview(p: Phaser.Input.Pointer): void {
    const zone = this.placedZones.find((z) => z.dbId === this.draggingZoneId);
    if (!zone) return;
    const { worldX, worldY } = this.pointerToWorld(p);
    const tileX = Math.floor(worldX / TILE);
    const tileY = Math.floor(worldY / TILE);
    const dx = tileX - this.dragZoneStartTileX;
    const dy = tileY - this.dragZoneStartTileY;
    zone.x = this.dragZoneOrigX + dx;
    zone.y = this.dragZoneOrigY + dy;
    this.redrawZoneGraphics(zone);
    this.drawSelectedHighlight(zone);
    zone.label.setPosition(zone.x * TILE + 4, zone.y * TILE + 2);
  }

  private finishZoneDrag(): void {
    const id = this.draggingZoneId;
    this.draggingZoneId = null;
    if (!id) return;
    const zone = this.placedZones.find((z) => z.dbId === id);
    if (!zone) return;
    if (zone.x === this.dragZoneOrigX && zone.y === this.dragZoneOrigY) {
      // Simple clic : sélection, la fenêtre de la zone s'ouvre.
      EventBus.emit('editor:zone-selected', {
        id, type: zone.type, name: zone.name, properties: zone.properties,
        x: zone.x, y: zone.y, width: zone.width, height: zone.height,
      });
      return;
    }

    // Prévisualisation uniquement — rien n'est écrit en DB tant que la
    // modal n'est pas validée. Le visuel reste à la position glissée ;
    // 'editor:zone-move-rejected' revert si la modal est annulée.
    EventBus.emit('editor:zone-move-preview', {
      id, type: zone.type, name: zone.name, properties: zone.properties,
      x: zone.x, y: zone.y, width: zone.width, height: zone.height,
      revertX: this.dragZoneOrigX, revertY: this.dragZoneOrigY,
    });
  }

  private clearSelectedZoneHighlight(): void {
    if (!this.selectedZoneId) return;
    const zone = this.placedZones.find((z) => z.dbId === this.selectedZoneId);
    if (zone) {
      this.redrawZoneGraphics(zone);
    }
    this.selectedZoneId = null;
  }

  private drawSelectedHighlight(zone: PlacedZone): void {
    zone.graphics.lineStyle(3, 0xFFFFFF, 1);
    zone.graphics.strokeRect(zone.x * TILE, zone.y * TILE, zone.width * TILE, zone.height * TILE);
  }

  private redrawZoneGraphics(zone: PlacedZone): void {
    const color = ZONE_COLORS[zone.type];
    zone.graphics.clear();
    if (zone.doorSprite) {
      zone.doorSprite.setPosition(zone.x * TILE, (zone.y + zone.height) * TILE);
      zone.doorSprite.setDepth(100 + ((zone.y + zone.height) * TILE) / 10);
    }
    zone.graphics.fillStyle(color, zone.doorSprite ? 0 : 0.3);
    zone.graphics.fillRect(zone.x * TILE, zone.y * TILE, zone.width * TILE, zone.height * TILE);
    zone.graphics.lineStyle(2, color, 0.8);
    zone.graphics.strokeRect(zone.x * TILE, zone.y * TILE, zone.width * TILE, zone.height * TILE);
  }

  // ── Bureaux personnels ─────────────────────────────────
  // Aménagés par les membres en jeu : l'admin les voit tels quels (contour +
  // prénom) et peut les glisser (modes Sélection et Emplacements, startDeskDrag) ;
  // modération : Admin › Bureaux.

  private renderPersonalDesks(desks: GameEventMap['editor:personal-desks']['desks']): void {
    this.personalDesks = desks;
    const generation = ++this.personalDeskGeneration;
    for (const obj of this.personalDeskObjects) obj.destroy();
    this.personalDeskObjects = [];

    const visible = desks.filter((d) => !d.outOfArea);
    const color = ZONE_COLORS['desk-area'];
    for (const desk of visible) {
      const g = this.add.graphics().setDepth(9996);
      g.lineStyle(2, color, 1);
      g.strokeRect(desk.tileX * TILE, desk.tileY * TILE, desk.width * TILE, desk.height * TILE);
      const label = this.add.text(desk.tileX * TILE + 4, (desk.tileY + desk.height) * TILE - 2, desk.displayName, {
        fontSize: '11px', fontFamily: 'Arial', color: '#1a1a2e',
        backgroundColor: `#${color.toString(16).padStart(6, '0')}`, padding: { x: 3, y: 1 },
      }).setOrigin(0, 1).setDepth(9998);
      this.personalDeskObjects.push(g, label);
    }

    const placed = visible.flatMap((d) => {
      const offsets = deskItemDepthOffsets(d.items);
      return d.items.map((item, i) => ({ desk: d, item, entry: findFurniture(item.furnitureId), offset: offsets[i] ?? 0 }));
    })
      .filter((p) => p.entry !== undefined);
    void ensureFurnitureTextures(this, placed.map((p) => p.entry!)).then(() => {
      if (generation !== this.personalDeskGeneration || !this.sys?.displayList) return; // rendu plus récent, ou scène détruite
      for (const { desk, item, entry, offset } of placed) {
        if (!hasFurnitureTexture(this, entry!)) continue;
        const bottomY = (desk.tileY + item.dy + 1) * TILE;
        const sprite = spawnFurnitureImage(this, entry!, (desk.tileX + item.dx) * TILE, bottomY);
        sprite.setDepth(item.layer === 'floor' ? FLOOR_DEPTHS.floorObjects : 100 + bottomY / 10 + offset);
        this.personalDeskObjects.push(sprite);
      }
    });
  }

  // ── Emplacements de bureaux (mode « Emplacements ») ───────
  // Libres : contour pointillé + « Libre ». Pris : le bureau du membre est déjà
  // affiché (renderPersonalDesks). Clic sur une case vide d'un espace bureaux
  // = ajouter ; glisser un emplacement = le déplacer (son bureau suit) ; clic
  // droit = le retirer. Validation (chevauchement, obstacles) : API.

  private renderDeskSlots(): void {
    for (const obj of this.deskSlotObjects) obj.destroy();
    this.deskSlotObjects = [];
    const active = this.currentMode === 'slots';
    const color = ZONE_COLORS['desk-area'];
    for (const slot of this.deskSlots) {
      if (slot.outOfArea) continue;
      const x = slot.tileX * TILE, y = slot.tileY * TILE, w = slot.width * TILE, h = slot.height * TILE;
      const g = this.add.graphics().setDepth(9995);
      if (slot.deskId) {
        // Pris : seulement repérable en mode « Emplacements » (on peut le déplacer).
        if (!active) { g.destroy(); continue; }
        g.lineStyle(2, color, 0.9);
        g.strokeRect(x + 1, y + 1, w - 2, h - 2);
      } else {
        // Contraste sur le fond orange de l'espace : trait sombre doublé de blanc.
        g.fillStyle(0xffffff, active ? 0.18 : 0.08);
        g.fillRect(x + 2, y + 2, w - 4, h - 4);
        const dash = 5, gap = 4;
        const dashed = (inset: number, width: number, c: number, alpha: number) => {
          g.lineStyle(width, c, alpha);
          const x1 = x + inset, y1 = y + inset, x2 = x + w - inset, y2 = y + h - inset;
          const line = (ax: number, ay: number, bx: number, by: number) => {
            const len = Math.hypot(bx - ax, by - ay);
            for (let d = 0; d < len; d += dash + gap) {
              const a = d / len, b = Math.min(d + dash, len) / len;
              g.lineBetween(ax + (bx - ax) * a, ay + (by - ay) * a, ax + (bx - ax) * b, ay + (by - ay) * b);
            }
          };
          line(x1, y1, x2, y1); line(x2, y1, x2, y2); line(x2, y2, x1, y2); line(x1, y2, x1, y1);
        };
        dashed(3, 1, 0xffffff, active ? 0.9 : 0.6);
        dashed(2, active ? 2 : 1, 0x1a1a2e, active ? 0.85 : 0.5);
        const label = this.add.text(x + w / 2, y + h / 2, 'Libre', {
          fontSize: '11px', fontFamily: 'Arial', color: '#1a1a2e',
          backgroundColor: '#ffffff', padding: { x: 3, y: 1 },
        }).setOrigin(0.5).setAlpha(active ? 1 : 0.75).setDepth(9996);
        this.deskSlotObjects.push(label);
      }
      this.deskSlotObjects.push(g);
    }
  }

  private slotAtTile(tileX: number, tileY: number) {
    return this.deskSlots.find((s) => !s.outOfArea
      && tileX >= s.tileX && tileX < s.tileX + s.width && tileY >= s.tileY && tileY < s.tileY + s.height) ?? null;
  }

  /** Clic en mode « Emplacements » ; false = rien à faire ici (la vue se déplace). */
  private startSlotGesture(p: Phaser.Input.Pointer): boolean {
    if (this.startDeskDrag(p)) return true;
    const { x: tileX, y: tileY } = this.pointerTile(p);
    const slot = this.slotAtTile(tileX, tileY);
    if (slot) {
      this.beginDrag('slot', slot.id, null, slot, tileX, tileY);
      return true;
    }
    const area = this.placedZones.find((z) => z.type === 'desk-area'
      && tileX >= z.x && tileX < z.x + z.width && tileY >= z.y && tileY < z.y + z.height);
    if (!area) return false;
    EventBus.emit('editor:slot-add', { areaZoneId: area.dbId, x: tileX - area.x, y: tileY - area.y });
    return true;
  }

  /**
   * Attraper un bureau à la souris : un bureau sur un emplacement se glisse
   * avec son emplacement, un bureau posé hors emplacement seul.
   */
  private startDeskDrag(p: Phaser.Input.Pointer): boolean {
    const { x: tileX, y: tileY } = this.pointerTile(p);
    const desk = this.personalDesks.find((d) => !d.outOfArea
      && tileX >= d.tileX && tileX < d.tileX + d.width && tileY >= d.tileY && tileY < d.tileY + d.height);
    if (!desk) return false;
    const slot = desk.slotId ? this.deskSlots.find((s) => s.id === desk.slotId && !s.outOfArea) : undefined;
    if (slot) this.beginDrag('slot', slot.id, desk.id, slot, tileX, tileY);
    else this.beginDrag('desk', desk.id, desk.id, desk, tileX, tileY);
    return true;
  }

  private beginDrag(
    kind: 'slot' | 'desk', id: string, deskId: string | null,
    rect: { x: number; y: number; tileX: number; tileY: number; width: number; height: number },
    tileX: number, tileY: number,
  ): void {
    this.slotDrag = {
      kind, id, deskId, offsetX: tileX - rect.tileX, offsetY: tileY - rect.tileY, pointerX: tileX, pointerY: tileY,
      areaX: rect.tileX - rect.x, areaY: rect.tileY - rect.y,
      startTileX: rect.tileX, startTileY: rect.tileY, tileX: rect.tileX, tileY: rect.tileY,
      width: rect.width, height: rect.height, ghost: this.add.graphics().setDepth(9997),
    };
    this.drawSlotGhost();
  }

  /** Emplacement libre sous la souris où ranger le bureau glissé (autre que le sien). */
  private dropSlot() {
    const d = this.slotDrag!;
    if (!d.deskId) return null;
    const target = this.slotAtTile(d.pointerX, d.pointerY);
    return target && !target.deskId && target.id !== d.id ? target : null;
  }

  private updateSlotDrag(p: Phaser.Input.Pointer): void {
    const d = this.slotDrag!;
    const { x, y } = this.pointerTile(p);
    if (x === d.pointerX && y === d.pointerY) return;
    d.pointerX = x;
    d.pointerY = y;
    d.tileX = x - d.offsetX;
    d.tileY = y - d.offsetY;
    this.drawSlotGhost();
  }

  private drawSlotGhost(): void {
    const d = this.slotDrag!;
    const color = ZONE_COLORS['desk-area'];
    // Au-dessus d'un emplacement libre, le bureau s'y range : fantôme calé dessus.
    const target = this.dropSlot();
    const x = target ? target.tileX : d.tileX, y = target ? target.tileY : d.tileY;
    d.ghost.clear();
    d.ghost.fillStyle(target ? 0x4caf50 : color, 0.3);
    d.ghost.fillRect(x * TILE, y * TILE, d.width * TILE, d.height * TILE);
    d.ghost.lineStyle(2, target ? 0x4caf50 : color, 1);
    d.ghost.strokeRect(x * TILE, y * TILE, d.width * TILE, d.height * TILE);
  }

  private finishSlotDrag(): void {
    const d = this.slotDrag!;
    const target = this.dropSlot();
    this.cancelSlotDrag();
    if (target && d.deskId) {
      EventBus.emit('editor:desk-move', { id: d.deskId, slotId: target.id });
      return;
    }
    if (d.tileX === d.startTileX && d.tileY === d.startTileY) return; // simple clic
    const x = d.tileX - d.areaX, y = d.tileY - d.areaY;
    if (d.kind === 'slot') EventBus.emit('editor:slot-move', { id: d.id, x, y });
    else EventBus.emit('editor:desk-move', { id: d.id, x, y });
  }

  private cancelSlotDrag(): void {
    this.slotDrag?.ghost.destroy();
    this.slotDrag = null;
  }

  // ── Zone CRUD helpers (ME-3e/f) ────────────────────────

  private spawnZoneRect(dbId: string, type: ZoneType, name: string, properties: Record<string, unknown>, x: number, y: number, width: number, height: number): void {
    const color = ZONE_COLORS[type];
    const g = this.add.graphics();
    g.setDepth(9997);
    g.fillStyle(color, 0.3);
    g.fillRect(x * TILE, y * TILE, width * TILE, height * TILE);
    g.lineStyle(2, color, 0.8);
    g.strokeRect(x * TILE, y * TILE, width * TILE, height * TILE);

    const label = this.add.text(x * TILE + 4, y * TILE + 2, name, {
      fontSize: '11px',
      fontFamily: 'Arial',
      color: '#ffffff',
      backgroundColor: `#${color.toString(16).padStart(6, '0')}`,
      padding: { x: 3, y: 1 },
    });
    label.setDepth(9998);

    const zone: PlacedZone = { dbId, type, name, properties, x, y, width, height, graphics: g, label };
    // Ascenseur : même rendu qu'une porte, sprite de l'ascenseur du métro (2 × 2 cases sur un zone 2 × 1).
    const doorSpriteId = type === 'door' ? (properties['doorSprite'] as string | undefined)
      : type === 'elevator' ? ELEVATOR_SPRITES.off : undefined;
    if (doorSpriteId && this.textures.exists(doorSpriteId)) {
      // Même ancrage qu'en jeu (ZoneScene.spawnDoorSprites) : bas-gauche sur
      // le bas de la zone. Rectangle de zone dessiné en contour seul pour
      // laisser voir la porte (voir redrawZoneGraphics).
      zone.doorSprite = this.add.image(x * TILE, (y + height) * TILE, doorSpriteId, 0).setOrigin(0, 1);
      zone.doorSprite.setDepth(100 + ((y + height) * TILE) / 10);
      this.redrawZoneGraphics(zone);
    }
    this.placedZones.push(zone);
  }

  private removeZoneRect(dbId: string): void {
    const idx = this.placedZones.findIndex((z) => z.dbId === dbId);
    if (idx === -1) return;
    const zone = this.placedZones[idx]!;
    zone.graphics.destroy();
    zone.label.destroy();
    zone.doorSprite?.destroy();
    this.placedZones.splice(idx, 1);
    if (this.selectedZoneId === dbId) this.selectedZoneId = null;
  }

  // ── ME-3f: Load zones from DB ──────────────────────────

  private loadZonesFromDB(): void {
    if (!this.zoneConfig) return;
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/zones`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
      },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((items: Array<{ id: string; type: string; name: string; properties: Record<string, unknown>; x: number; y: number; width: number; height: number }>) => {
        for (const item of items) {
          this.spawnZoneRect(item.id, item.type as ZoneType, item.name, item.properties, item.x, item.y, item.width, item.height);
        }
        EventBus.emit('editor:zones-loaded', { zones: items });
        console.log(`[EditorScene] loaded ${items.length} zones from DB`);
      })
      .catch((err) => console.warn('[EditorScene] load zones failed:', err));
  }

  shutdown() {
    EventBus.off('editor:set-mode');
    EventBus.off('editor:zoom');
    EventBus.off('editor:select-furniture');
    EventBus.off('editor:rotate-placed-furniture');
    EventBus.off('editor:select-zone-type');
    EventBus.off('editor:zone-created');
    EventBus.off('editor:zone-updated');
    EventBus.off('editor:zone-deleted');
    EventBus.off('editor:zone-move-rejected');
    EventBus.off('editor:toggle-grid');
    EventBus.off('editor:select-wall-style');
    EventBus.off('editor:select-door');
    EventBus.off('editor:door-placed');
    EventBus.off('editor:floor-settings');
    EventBus.off('editor:labels-updated');
    EventBus.off('editor:personal-desks');
    EventBus.off('editor:desk-slots');
    this.cancelSlotDrag();
    for (const obj of this.deskSlotObjects) obj.destroy();
    this.deskSlotObjects = [];
    this.labelRenderer?.clear();
    this.floorPreview?.destroy();
    this.clearGhost();
    this.selectionOutline?.destroy();
    this.selectionOutline = null;
    this.selectedPlaced = null;
    this.clearWallGhost();
    this.clearDoorGhost();
    this.zoneDrawPreview?.destroy();
    for (const z of this.placedZones) {
      z.graphics.destroy();
      z.label.destroy();
      z.doorSprite?.destroy();
    }
    this.placedZones = [];
    this.wallRenderer?.clear();
    this.wallRenderer = null;
    this.placedWalls = [];
    this.gridGraphics?.destroy();
    this.gridGraphics = null;
  }
}
